import { useState, useRef } from 'react';
import { ChangeEvent, DragEvent } from 'react';
import { Upload, Loader2 } from 'lucide-react';

import { cn } from '@/lib/utils';

interface FileUploadProps {
  onFileChange?: (files: FileList | null) => void;
  onChange?: (files: File[]) => void;
  value?: File[];
  acceptedTypes?: string;
  maxSize?: number;
  multiple?: boolean;
  hasError?: boolean;
  className?: string;
  disabled?: boolean;
  isLoading?: boolean;
}

const FileUpload = ({
  className,
  onFileChange,
  onChange,
  acceptedTypes = '.zip,.pdf',
  multiple = false,
  disabled = false,
  isLoading = false,
}: FileUploadProps) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [, setSelectedFiles] = useState<FileList | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleDragEnter = (e: DragEvent) => {
    if (disabled || isLoading) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: DragEvent) => {
    if (disabled || isLoading) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDragOver = (e: DragEvent) => {
    if (disabled || isLoading) return;
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: DragEvent) => {
    if (disabled || isLoading) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      // If multiple is false, only take the first file
      const fileArray = multiple ? Array.from(files) : [files[0]];
      setSelectedFiles(files);
      onFileChange?.(files);
      onChange?.(fileArray);
    }
  };

  const handleFileSelect = (e: ChangeEvent<HTMLInputElement>) => {
    if (disabled || isLoading) return;
    const files = e.target.files;
    if (files) {
      // If multiple is false, only take the first file
      const fileArray = multiple ? Array.from(files) : [files[0]];
      setSelectedFiles(files);
      onFileChange?.(files);
      onChange?.(fileArray);
    }
  };

  return (
    <div className="w-full">
      <div
        className={cn(
          'relative flex w-full flex-col items-center justify-center rounded-lg border-2 border-dashed border-green-300 px-6 py-3 text-center transition-all duration-200 ease-in-out',
          disabled || isLoading
            ? 'cursor-not-allowed border-gray-200 bg-gray-100'
            : 'cursor-pointer border-gray-300 bg-gray-50 hover:bg-gray-100',
          !disabled && isDragOver && 'bg-neutral-400',
          className,
        )}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        <input
          ref={inputRef}
          type="file"
          accept={acceptedTypes}
          multiple={multiple}
          onChange={handleFileSelect}
          disabled={disabled || isLoading}
          className={cn(
            'absolute inset-0 h-full w-full opacity-0',
            disabled || isLoading ? 'cursor-not-allowed' : 'cursor-pointer',
          )}
          tabIndex={-1}
          aria-hidden="true"
        />

        <div className="flex items-center gap-2">
          {isLoading ? (
            <Loader2 className="h-5 w-5 animate-spin text-green-500" />
          ) : (
            <Upload className="h-5 w-5" />
          )}
          <p className={'text-sm font-medium'}>
            {isLoading
              ? 'Uploading...'
              : disabled
                ? 'Limit of (1) file reached.'
                : multiple
                  ? 'Choose files or drag them here'
                  : 'Choose a file or drag it here'}
          </p>
        </div>
      </div>
    </div>
  );
};

export { FileUpload, type FileUploadProps };
